import * as React from 'react';
import * as SwitchPrimitive from '@radix-ui/react-switch';
import { cn } from '@/lib/utils';

interface SwitchProps extends React.ComponentPropsWithoutRef<typeof SwitchPrimitive.Root> {
  label?: string;
  description?: string;
}

const Switch = React.forwardRef<
  React.ComponentRef<typeof SwitchPrimitive.Root>,
  SwitchProps
>(({ className, label, description, id, ...props }, ref) => {
  const generatedId = React.useId();
  const switchId = id ?? generatedId;

  if (label) {
    return (
      <div className="flex items-center justify-between gap-3 min-h-[44px]">
        <div className="flex-1">
          <label
            htmlFor={switchId}
            className="text-sm font-medium text-raios-text cursor-pointer"
          >
            {label}
          </label>
          {description && (
            <p className="text-xs text-raios-muted mt-0.5">{description}</p>
          )}
        </div>
        <SwitchPrimitive.Root
          id={switchId}
          ref={ref}
          className={cn(
            'peer inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-raios-accent/40 focus-visible:ring-offset-2 focus-visible:ring-offset-raios-bg',
            'disabled:cursor-not-allowed disabled:opacity-50',
            'data-[state=checked]:bg-raios-accent data-[state=unchecked]:bg-raios-border',
            className
          )}
          {...props}
        >
          <SwitchPrimitive.Thumb
            className={cn(
              'pointer-events-none block h-5 w-5 rounded-full bg-white shadow-raios-sm ring-0 transition-transform',
              'data-[state=checked]:translate-x-5 data-[state=unchecked]:translate-x-0'
            )}
          />
        </SwitchPrimitive.Root>
      </div>
    );
  }

  return (
    <SwitchPrimitive.Root
      id={switchId}
      ref={ref}
      className={cn(
        'peer inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-raios-accent/40 focus-visible:ring-offset-2 focus-visible:ring-offset-raios-bg',
        'disabled:cursor-not-allowed disabled:opacity-50',
        'data-[state=checked]:bg-raios-accent data-[state=unchecked]:bg-raios-border',
        className
      )}
      {...props}
    >
      <SwitchPrimitive.Thumb
        className={cn(
          'pointer-events-none block h-5 w-5 rounded-full bg-white shadow-raios-sm ring-0 transition-transform',
          'data-[state=checked]:translate-x-5 data-[state=unchecked]:translate-x-0'
        )}
      />
    </SwitchPrimitive.Root>
  );
});

Switch.displayName = 'Switch';

export { Switch };
export type { SwitchProps };
